<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import dayjs from "dayjs";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Stripe Reconciliation");

const currentQuarterStart = () => {
  const now = dayjs();
  return now.month(Math.floor(now.month() / 3) * 3).startOf("month");
};

const startDate = ref(currentQuarterStart().format("YYYY-MM-DD"));
const endDate = ref(
  currentQuarterStart().add(2, "month").endOf("month").format("YYYY-MM-DD"),
);
const matchStatus = ref("unmatched");
const loading = ref(false);
const syncing = ref(false);
const error = ref("");
const workspace = ref(null);
const activeTransaction = ref(null);
const activeMode = ref("match");
const invoiceQuery = ref("");
const invoiceResults = ref([]);
const selectedInvoice = ref(null);
const allocationAmount = ref("");
const searchingInvoices = ref(false);
const saving = ref(false);
const customerQuery = ref("");
const customerResults = ref([]);
const selectedCustomer = ref(null);
const searchingCustomers = ref(false);

const createForm = reactive({
  reference_type_id: "",
  name: "",
  description: "",
  issue_date: "",
  vat_treatment: "standard_20",
});

const summary = computed(() => workspace.value?.summary ?? {});
const transactions = computed(() => workspace.value?.transactions ?? []);
const payouts = computed(() => workspace.value?.payouts ?? []);
const referenceTypes = computed(() => workspace.value?.reference_types ?? []);
const activeReferenceType = computed(() =>
  referenceTypes.value.find(
    (type) => Number(type.id) === Number(createForm.reference_type_id),
  ),
);

const money = (value, currency = "gbp") =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: String(currency || "gbp").toUpperCase(),
  }).format(Number(value ?? 0) / 100);

const date = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "—");
const invoiceLabel = (invoice) =>
  [
    invoice?.reference,
    invoice?.customer,
    invoice?.reference_name,
    money(invoice?.total_amount),
  ]
    .filter(Boolean)
    .join(" · ");

const allocationMinorUnits = computed(() => {
  const amount = Number(allocationAmount.value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
});

const previewAmounts = computed(() => {
  const transaction = activeTransaction.value;
  if (!transaction) return { subtotal: 0, vat: 0, gross: 0 };
  const gross = allocationMinorUnits.value;
  if (createForm.vat_treatment === "standard_20") {
    const subtotal = Math.round(gross / 1.2);
    return { subtotal, vat: gross - subtotal, gross };
  }
  return { subtotal: gross, vat: 0, gross };
});

const loadWorkspace = async () => {
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.get("/api/stripe-reconciliation", {
      params: {
        start_date: startDate.value,
        end_date: endDate.value,
        status: matchStatus.value,
      },
    });
    workspace.value = data;
  } catch (err) {
    error.value =
      err?.response?.data?.message ?? "Unable to load Stripe reconciliation.";
  } finally {
    loading.value = false;
  }
};

const syncStripe = async () => {
  syncing.value = true;
  error.value = "";
  try {
    await apiClient.post("/api/stripe-reconciliation/sync", {
      start_date: startDate.value,
      end_date: endDate.value,
    });
    await loadWorkspace();
  } catch (err) {
    error.value = err?.response?.data?.message ?? "Unable to sync Stripe.";
  } finally {
    syncing.value = false;
  }
};

const closeModal = (force = false) => {
  if (saving.value && !force) return;
  activeTransaction.value = null;
  selectedInvoice.value = null;
  allocationAmount.value = "";
  invoiceResults.value = [];
  selectedCustomer.value = null;
  customerResults.value = [];
};

const searchInvoices = async (useExactAmount = false) => {
  if (!activeTransaction.value) return;
  searchingInvoices.value = true;
  try {
    const { data } = await apiClient.get(
      "/api/stripe-reconciliation/invoices/search",
      {
        params: {
          query: useExactAmount
            ? undefined
            : invoiceQuery.value.trim() || undefined,
          amount: useExactAmount
            ? allocationMinorUnits.value ||
              Number(activeTransaction.value.remaining_gross_amount)
            : undefined,
          limit: 15,
        },
      },
    );
    invoiceResults.value = data ?? [];
  } finally {
    searchingInvoices.value = false;
  }
};

const openMatch = async (transaction) => {
  activeTransaction.value = transaction;
  activeMode.value = "match";
  allocationAmount.value = (
    Number(transaction.remaining_gross_amount) / 100
  ).toFixed(2);
  invoiceQuery.value =
    transaction.customer_name || transaction.customer_email || "";
  selectedInvoice.value = null;
  await searchInvoices(true);
};

const matchInvoice = async (allowAmountMismatch = false) => {
  if (!activeTransaction.value || !selectedInvoice.value) return;
  saving.value = true;
  try {
    await apiClient.put(
      `/api/stripe-reconciliation/transactions/${activeTransaction.value.id}/invoice`,
      {
        invoice_id: selectedInvoice.value.id,
        allocated_gross_amount: allocationMinorUnits.value,
        allow_amount_mismatch: allowAmountMismatch,
      },
    );
    closeModal(true);
    await loadWorkspace();
  } catch (err) {
    const errors = err?.response?.data?.errors;
    const mismatch = err?.response?.status === 422 && errors?.invoice_id;
    if (
      mismatch &&
      !allowAmountMismatch &&
      window.confirm(`${mismatch[0]} Continue with this invoice anyway?`)
    ) {
      saving.value = false;
      await matchInvoice(true);
      return;
    }
    alert(
      err?.response?.data?.message ??
        mismatch?.[0] ??
        errors?.allocated_gross_amount?.[0] ??
        "Unable to match the invoice.",
    );
  } finally {
    saving.value = false;
  }
};

const openCreate = (transaction) => {
  activeTransaction.value = transaction;
  activeMode.value = "create";
  allocationAmount.value = (
    Number(transaction.remaining_gross_amount) / 100
  ).toFixed(2);
  selectedCustomer.value = transaction.suggested_customer ?? null;
  customerQuery.value =
    transaction.customer_name || transaction.customer_email || "";
  const preferredType =
    referenceTypes.value.find((type) =>
      String(type.name || "")
        .toLowerCase()
        .includes("train"),
    ) ??
    referenceTypes.value.find((type) => type.is_vatable) ??
    referenceTypes.value[0];
  Object.assign(createForm, {
    reference_type_id: preferredType?.id ?? "",
    name:
      transaction.description ||
      `Stripe payment ${transaction.stripe_charge_id || ""}`,
    description: transaction.description || "Training course booking",
    issue_date: dayjs(transaction.paid_at).format("YYYY-MM-DD"),
    vat_treatment: preferredType?.is_vatable ? "standard_20" : "no_vat",
  });
};

watch(activeReferenceType, (type) => {
  if (type)
    createForm.vat_treatment = type.is_vatable ? "standard_20" : "no_vat";
});

watch(selectedInvoice, (invoice) => {
  if (!invoice || !activeTransaction.value) return;
  allocationAmount.value = (
    Math.min(
      Number(invoice.total_amount),
      Number(activeTransaction.value.remaining_gross_amount),
    ) / 100
  ).toFixed(2);
});

const searchCustomers = async () => {
  if (customerQuery.value.trim().length < 2) return;
  searchingCustomers.value = true;
  try {
    const { data } = await apiClient.get("/api/customer/search", {
      params: { query: customerQuery.value.trim(), limit: 15 },
    });
    customerResults.value = Array.isArray(data) ? data : (data?.data ?? []);
  } finally {
    searchingCustomers.value = false;
  }
};

const createCustomerFromStripe = async () => {
  const company =
    customerQuery.value.trim() || activeTransaction.value?.customer_name;
  if (!company) {
    alert("Enter the customer or delegate name first.");
    return;
  }
  if (!window.confirm(`Create a new Neptune customer named “${company}”?`))
    return;
  searchingCustomers.value = true;
  try {
    const { data } = await apiClient.post("/api/customer", {
      company,
      notes: `Created while reconciling Stripe payment ${activeTransaction.value?.stripe_charge_id || activeTransaction.value?.stripe_balance_transaction_id}. Payer email: ${activeTransaction.value?.customer_email || "not supplied"}.`,
    });
    selectedCustomer.value = data;
    customerResults.value = [];
  } catch (err) {
    alert(err?.response?.data?.message ?? "Unable to create the customer.");
  } finally {
    searchingCustomers.value = false;
  }
};

const createInvoice = async () => {
  if (
    !activeTransaction.value ||
    !selectedCustomer.value?.id ||
    !createForm.reference_type_id
  ) {
    alert("Select a customer and reference type.");
    return;
  }
  saving.value = true;
  try {
    await apiClient.post(
      `/api/stripe-reconciliation/transactions/${activeTransaction.value.id}/invoice`,
      {
        customer_id: selectedCustomer.value.id,
        allocated_gross_amount: allocationMinorUnits.value,
        ...createForm,
      },
    );
    closeModal(true);
    await loadWorkspace();
  } catch (err) {
    const validationMessage = Object.values(
      err?.response?.data?.errors ?? {},
    ).flat()[0];
    alert(
      validationMessage ??
        err?.response?.data?.message ??
        "Unable to create the Neptune invoice.",
    );
  } finally {
    saving.value = false;
  }
};

const detachInvoice = async (transaction, invoice) => {
  if (
    !window.confirm(
      "Remove this Stripe-to-Neptune invoice match? The invoice itself will be kept.",
    )
  )
    return;
  await apiClient.delete(
    `/api/stripe-reconciliation/transactions/${transaction.id}/invoices/${invoice.id}`,
  );
  await loadWorkspace();
};

const matchPayout = async (
  payout,
  bankTransaction,
  allowAmountMismatch = false,
) => {
  try {
    await apiClient.put(
      `/api/stripe-reconciliation/payouts/${payout.id}/bank-transaction`,
      {
        bank_transaction_id: bankTransaction.id,
        allow_amount_mismatch: allowAmountMismatch,
      },
    );
    await loadWorkspace();
  } catch (err) {
    const message = err?.response?.data?.errors?.bank_transaction_id?.[0];
    if (
      message &&
      !allowAmountMismatch &&
      window.confirm(`${message} Continue anyway?`)
    ) {
      await matchPayout(payout, bankTransaction, true);
      return;
    }
    alert(
      err?.response?.data?.message ?? message ?? "Unable to match the payout.",
    );
  }
};

const detachPayout = async (payout) => {
  if (!window.confirm("Remove this payout-to-bank match?")) return;
  await apiClient.delete(
    `/api/stripe-reconciliation/payouts/${payout.id}/bank-transaction`,
  );
  await loadWorkspace();
};

onMounted(loadWorkspace);
</script>

<template>
  <div class="space-y-6">
    <section class="box space-y-4">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p
            class="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500"
          >
            Super user
          </p>
          <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            Stripe invoice reconciliation
          </h1>
          <p class="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-300">
            Match the full amount paid by each customer to Neptune. Stripe fees
            remain separate and payouts reconcile to the net bank credit.
          </p>
        </div>
        <button
          class="btn accent"
          :disabled="syncing || loading"
          @click.prevent="syncStripe"
        >
          {{ syncing ? "Syncing Stripe…" : "Sync Stripe" }}
        </button>
      </div>

      <div class="grid gap-3 md:grid-cols-4">
        <label class="text-sm text-slate-600 dark:text-slate-300">
          <span class="mb-1 block text-xs font-semibold uppercase">From</span>
          <input v-model="startDate" type="date" class="input w-full" />
        </label>
        <label class="text-sm text-slate-600 dark:text-slate-300">
          <span class="mb-1 block text-xs font-semibold uppercase">To</span>
          <input v-model="endDate" type="date" class="input w-full" />
        </label>
        <label class="text-sm text-slate-600 dark:text-slate-300">
          <span class="mb-1 block text-xs font-semibold uppercase"
            >Invoice status</span
          >
          <select v-model="matchStatus" class="input w-full">
            <option value="unmatched">Needs allocation</option>
            <option value="matched">Fully allocated</option>
            <option value="all">All</option>
          </select>
        </label>
        <div class="flex items-end">
          <button
            class="btn w-full"
            :disabled="loading"
            @click.prevent="loadWorkspace"
          >
            {{ loading ? "Loading…" : "Apply range" }}
          </button>
        </div>
      </div>

      <p
        v-if="workspace && !workspace.configured"
        class="rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
      >
        Stripe is not configured on the server. Add the Stripe secret key before
        syncing.
      </p>
      <p v-if="error" class="rounded-lg bg-red-50 p-3 text-sm text-red-700">
        {{ error }}
      </p>
    </section>

    <section class="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <div class="box">
        <div class="text-xs uppercase text-slate-500">Payments</div>
        <div class="mt-1 text-xl font-semibold">
          {{ summary.payments ?? 0 }}
        </div>
      </div>
      <div class="box">
        <div class="text-xs uppercase text-slate-500">Needs allocation</div>
        <div class="mt-1 text-xl font-semibold text-amber-600">
          {{ summary.unmatched_payments ?? 0 }}
        </div>
        <div class="text-xs text-slate-400">
          {{ summary.partially_allocated_payments ?? 0 }} part allocated
        </div>
      </div>
      <div class="box">
        <div class="text-xs uppercase text-slate-500">Gross sales</div>
        <div class="mt-1 text-xl font-semibold">
          {{ money(summary.gross_sales) }}
        </div>
      </div>
      <div class="box">
        <div class="text-xs uppercase text-slate-500">Known VAT</div>
        <div class="mt-1 text-xl font-semibold">{{ money(summary.vat) }}</div>
      </div>
      <div class="box">
        <div class="text-xs uppercase text-slate-500">Stripe fees</div>
        <div class="mt-1 text-xl font-semibold text-rose-600">
          {{ money(summary.fees) }}
        </div>
      </div>
    </section>

    <section class="box overflow-hidden">
      <div class="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Customer payments and refunds
          </h2>
          <p class="text-xs text-slate-500">
            Allocate each customer payment across its Neptune invoices. Every
            allocation keeps its own net and VAT treatment.
          </p>
        </div>
        <span class="text-sm text-slate-500"
          >{{ transactions.length }} shown</span
        >
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left text-sm">
          <thead class="border-b text-xs uppercase text-slate-500">
            <tr>
              <th class="px-3 py-3">Paid</th>
              <th class="px-3 py-3">Customer / description</th>
              <th class="px-3 py-3 text-right">Gross</th>
              <th class="px-3 py-3 text-right">VAT</th>
              <th class="px-3 py-3 text-right">Fee</th>
              <th class="px-3 py-3">Neptune invoice</th>
              <th class="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
            <tr
              v-for="transaction in transactions"
              :key="transaction.id"
              class="align-top"
            >
              <td class="whitespace-nowrap px-3 py-3">
                {{ date(transaction.paid_at) }}
                <div class="text-xs text-slate-400">{{ transaction.type }}</div>
              </td>
              <td class="max-w-md px-3 py-3">
                <div class="font-medium text-slate-900 dark:text-slate-100">
                  {{
                    transaction.customer_name ||
                    transaction.customer_email ||
                    "Stripe customer"
                  }}
                </div>
                <div class="mt-0.5 text-xs text-slate-500">
                  {{ transaction.description }}
                </div>
                <div class="mt-1 font-mono text-[10px] text-slate-400">
                  {{
                    transaction.stripe_invoice_id ||
                    transaction.stripe_charge_id ||
                    transaction.stripe_balance_transaction_id
                  }}
                </div>
              </td>
              <td class="whitespace-nowrap px-3 py-3 text-right font-semibold">
                {{ money(transaction.gross_amount, transaction.currency) }}
              </td>
              <td class="whitespace-nowrap px-3 py-3 text-right">
                <span v-if="transaction.vat_amount !== null">{{
                  money(transaction.vat_amount, transaction.currency)
                }}</span
                ><span v-else class="text-amber-600">Review</span>
              </td>
              <td class="whitespace-nowrap px-3 py-3 text-right text-rose-600">
                {{ money(transaction.fee_amount, transaction.currency) }}
              </td>
              <td class="max-w-xs px-3 py-3">
                <div v-if="transaction.invoices?.length" class="space-y-2">
                  <div
                    v-for="invoice in transaction.invoices"
                    :key="invoice.id"
                    class="rounded-lg border border-slate-200 p-2 dark:border-slate-700"
                  >
                    <router-link
                      :to="`/invoice/${invoice.id}`"
                      class="font-semibold text-cyan-700 hover:underline dark:text-cyan-300"
                      >{{ invoiceLabel(invoice) }}</router-link
                    >
                    <div class="mt-1 text-xs text-slate-500">
                      Allocated {{ money(invoice.allocated_gross_amount) }} ·
                      Net {{ money(invoice.allocated_net_amount) }} · VAT
                      {{ money(invoice.allocated_vat_amount) }}
                    </div>
                    <button
                      v-if="transaction.type === 'payment'"
                      class="mt-1 text-xs font-semibold text-rose-600 hover:underline"
                      @click.prevent="detachInvoice(transaction, invoice)"
                    >
                      Remove allocation
                    </button>
                  </div>
                </div>
                <span
                  v-if="transaction.allocation_status === 'unmatched'"
                  class="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800"
                  >Needs allocation</span
                >
                <div
                  v-else-if="transaction.allocation_status === 'partial'"
                  class="mt-2 text-xs font-semibold text-amber-600"
                >
                  {{ money(transaction.remaining_gross_amount) }} still to
                  allocate
                </div>
                <div
                  v-else-if="transaction.allocation_status === 'matched'"
                  class="mt-2 text-xs font-semibold text-emerald-600"
                >
                  Fully allocated
                </div>
              </td>
              <td class="whitespace-nowrap px-3 py-3 text-right">
                <template
                  v-if="
                    transaction.type === 'payment' &&
                    Number(transaction.gross_amount) > 0
                  "
                >
                  <button
                    v-if="Number(transaction.remaining_gross_amount) > 0"
                    class="btn mr-1 text-xs"
                    @click.prevent="openMatch(transaction)"
                  >
                    Match
                  </button>
                  <button
                    v-if="Number(transaction.remaining_gross_amount) > 0"
                    class="btn accent text-xs"
                    @click.prevent="openCreate(transaction)"
                  >
                    Create
                  </button>
                </template>
              </td>
            </tr>
            <tr v-if="!transactions.length">
              <td colspan="7" class="px-3 py-10 text-center text-slate-500">
                No Stripe records match this range and filter.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="box space-y-3">
      <div>
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Payouts to the bank
        </h2>
        <p class="text-xs text-slate-500">
          Each net payout should match one incoming bank transaction. Its
          component payments drive sales and VAT.
        </p>
      </div>
      <div
        v-for="payout in payouts"
        :key="payout.id"
        class="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div class="font-semibold">
              {{ payout.stripe_id }} · {{ date(payout.arrival_date) }}
            </div>
            <div class="mt-1 text-xs text-slate-500">
              {{ payout.transaction_count }} components ·
              {{ payout.matched_invoice_count }} invoice-linked
            </div>
          </div>
          <div class="text-right">
            <div class="text-lg font-semibold">
              {{ money(payout.amount, payout.currency) }}
            </div>
            <div
              :class="
                payout.reconciliation_difference
                  ? 'text-red-600'
                  : 'text-emerald-600'
              "
              class="text-xs"
            >
              Stripe composition difference
              {{ money(payout.reconciliation_difference, payout.currency) }}
            </div>
          </div>
        </div>
        <div class="mt-3 grid gap-2 text-sm sm:grid-cols-4">
          <div>
            Gross <strong>{{ money(payout.gross_payments) }}</strong>
          </div>
          <div>
            Refunds <strong>{{ money(payout.refunds) }}</strong>
          </div>
          <div>
            Fees <strong>{{ money(payout.fees) }}</strong>
          </div>
          <div>
            Settlement <strong>{{ money(payout.settlement_total) }}</strong>
          </div>
        </div>
        <p
          v-if="!payout.automatic"
          class="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800"
        >
          Stripe could not expose this manual or instant payout's component
          transactions. Review its composition before relying on the
          reconciliation difference.
        </p>
        <div
          class="mt-3 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-900/40"
        >
          <div
            v-if="payout.bank_transaction"
            class="flex flex-wrap items-center justify-between gap-2"
          >
            <span
              >Bank #{{ payout.bank_transaction.id }} ·
              {{ date(payout.bank_transaction.transaction_date) }} ·
              {{ payout.bank_transaction.description }} ·
              {{ money(payout.bank_transaction.paid_in) }}</span
            >
            <button class="btn text-xs" @click.prevent="detachPayout(payout)">
              Remove bank match
            </button>
          </div>
          <div
            v-else-if="payout.suggested_bank_transactions.length"
            class="space-y-2"
          >
            <div class="text-xs font-semibold uppercase text-slate-500">
              Exact bank matches
            </div>
            <div
              v-for="candidate in payout.suggested_bank_transactions"
              :key="candidate.id"
              class="flex flex-wrap items-center justify-between gap-2"
            >
              <span
                >#{{ candidate.id }} · {{ date(candidate.transaction_date) }} ·
                {{ candidate.description }} ·
                {{ money(candidate.paid_in) }}</span
              >
              <button
                class="btn accent text-xs"
                @click.prevent="matchPayout(payout, candidate)"
              >
                Connect payout
              </button>
            </div>
          </div>
          <div v-else class="text-slate-500">
            No exact incoming bank transaction found. Sync/import bank
            transactions, then reload this period.
          </div>
        </div>
      </div>
      <p v-if="!payouts.length" class="py-6 text-center text-sm text-slate-500">
        No payouts found for this period.
      </p>
    </section>

    <div
      v-if="activeTransaction"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
      @click.self="closeModal()"
    >
      <div
        class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h2 class="text-xl font-semibold">
              {{
                activeMode === "match"
                  ? "Match existing invoice"
                  : "Create historical invoice"
              }}
            </h2>
            <p class="mt-1 text-sm text-slate-500">
              {{ activeTransaction.description }} ·
              {{ money(activeTransaction.gross_amount) }} paid ·
              {{ money(activeTransaction.remaining_gross_amount) }} unallocated
            </p>
          </div>
          <button class="btn" @click.prevent="closeModal()">Close</button>
        </div>

        <label class="mt-5 block">
          <span class="text-xs font-semibold uppercase text-slate-500"
            >Amount to allocate</span
          >
          <div class="mt-1 flex items-center gap-2">
            <span class="text-slate-500">£</span>
            <input
              v-model="allocationAmount"
              type="number"
              min="0.01"
              :max="Number(activeTransaction.remaining_gross_amount) / 100"
              step="0.01"
              class="input w-48"
            />
            <span class="text-xs text-slate-500">
              maximum {{ money(activeTransaction.remaining_gross_amount) }}
            </span>
          </div>
        </label>

        <div v-if="activeMode === 'match'" class="mt-5 space-y-4">
          <div class="flex gap-2">
            <input
              v-model="invoiceQuery"
              class="input flex-1"
              placeholder="Reference, customer, PO or description"
              @keyup.enter="searchInvoices(false)"
            /><button
              class="btn"
              :disabled="searchingInvoices"
              @click.prevent="searchInvoices(false)"
            >
              {{ searchingInvoices ? "Searching…" : "Search" }}</button
            ><button class="btn" @click.prevent="searchInvoices(true)">
              Exact amount
            </button>
          </div>
          <div
            class="max-h-80 divide-y overflow-y-auto rounded-lg border dark:border-slate-700"
          >
            <label
              v-for="invoice in invoiceResults"
              :key="invoice.id"
              class="flex cursor-pointer items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700"
              ><input
                v-model="selectedInvoice"
                type="radio"
                :value="invoice"
              /><span class="flex-1">{{ invoiceLabel(invoice) }}</span
              ><span
                :class="
                  Number(invoice.total_amount) === allocationMinorUnits
                    ? 'text-emerald-600'
                    : 'text-amber-600'
                "
                >{{
                  money(Number(invoice.total_amount) - allocationMinorUnits)
                }}
                difference</span
              ></label
            >
            <p
              v-if="!invoiceResults.length"
              class="p-5 text-center text-sm text-slate-500"
            >
              No matching invoices found.
            </p>
          </div>
          <div class="flex justify-end">
            <button
              class="btn accent"
              :disabled="
                saving || !selectedInvoice || allocationMinorUnits <= 0
              "
              @click.prevent="matchInvoice(false)"
            >
              {{ saving ? "Saving…" : "Save match" }}
            </button>
          </div>
        </div>

        <div v-else class="mt-5 space-y-4">
          <div>
            <label class="text-xs font-semibold uppercase text-slate-500"
              >Neptune customer</label
            >
            <div
              v-if="selectedCustomer"
              class="mt-1 flex items-center justify-between rounded-lg border p-3"
            >
              <strong>{{ selectedCustomer.company }}</strong
              ><button
                class="btn text-xs"
                @click.prevent="selectedCustomer = null"
              >
                Change
              </button>
            </div>
            <div v-else class="mt-1">
              <div class="flex gap-2">
                <input
                  v-model="customerQuery"
                  class="input flex-1"
                  placeholder="Search customer"
                  @keyup.enter="searchCustomers"
                /><button
                  class="btn"
                  :disabled="searchingCustomers"
                  @click.prevent="searchCustomers"
                >
                  Search</button
                ><button
                  class="btn"
                  :disabled="searchingCustomers"
                  @click.prevent="createCustomerFromStripe"
                >
                  Create new
                </button>
              </div>
              <div
                class="mt-2 max-h-40 divide-y overflow-y-auto rounded-lg border"
              >
                <button
                  v-for="customer in customerResults"
                  :key="customer.id"
                  class="block w-full p-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700"
                  @click.prevent="selectedCustomer = customer"
                >
                  {{ customer.company }}
                </button>
              </div>
            </div>
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <label
              ><span class="text-xs font-semibold uppercase text-slate-500"
                >Reference type</span
              ><select
                v-model="createForm.reference_type_id"
                class="input mt-1 w-full"
              >
                <option value="">Select type</option>
                <option
                  v-for="type in referenceTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.prefix }} · {{ type.name }}
                </option>
              </select></label
            ><label
              ><span class="text-xs font-semibold uppercase text-slate-500"
                >Issue date</span
              ><input
                v-model="createForm.issue_date"
                type="date"
                class="input mt-1 w-full"
            /></label>
          </div>
          <label class="block"
            ><span class="text-xs font-semibold uppercase text-slate-500"
              >Reference name</span
            ><input v-model="createForm.name" class="input mt-1 w-full"
          /></label>
          <label class="block"
            ><span class="text-xs font-semibold uppercase text-slate-500"
              >Invoice description</span
            ><textarea
              v-model="createForm.description"
              rows="3"
              class="input mt-1 w-full"
            ></textarea>
          </label>
          <div>
            <span class="text-xs font-semibold uppercase text-slate-500"
              >VAT treatment</span
            >
            <div class="mt-2 flex gap-4">
              <label
                ><input
                  v-model="createForm.vat_treatment"
                  type="radio"
                  value="standard_20"
                />
                Standard 20%</label
              ><label
                ><input
                  v-model="createForm.vat_treatment"
                  type="radio"
                  value="no_vat"
                />
                No VAT</label
              >
            </div>
          </div>
          <div
            class="grid grid-cols-3 gap-3 rounded-lg bg-slate-50 p-4 text-center dark:bg-slate-900/40"
          >
            <div>
              <div class="text-xs uppercase text-slate-500">Subtotal</div>
              <strong>{{ money(previewAmounts.subtotal) }}</strong>
            </div>
            <div>
              <div class="text-xs uppercase text-slate-500">VAT</div>
              <strong>{{ money(previewAmounts.vat) }}</strong>
            </div>
            <div>
              <div class="text-xs uppercase text-slate-500">Gross paid</div>
              <strong>{{ money(previewAmounts.gross) }}</strong>
            </div>
          </div>
          <div class="flex justify-end">
            <button
              class="btn accent"
              :disabled="
                saving ||
                !selectedCustomer ||
                !createForm.reference_type_id ||
                allocationMinorUnits <= 0
              "
              @click.prevent="createInvoice"
            >
              {{ saving ? "Creating…" : "Create paid invoice" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
